import { t } from "@lingui/core/macro";
import { api } from "@/lib/api-client";
import { CustomerSubscriptionCard } from "./CustomerSubscriptionCard";
import { Button, useDisclosure, Tabs, Tab } from "@heroui/react";
import { CustomerSubscriptionDrawer } from "./CustomerSubscriptionDrawer";
import { useMemo, useState } from "react";
import { GetCustomerSubscriptions } from "@/server-types";
import { VisitEventModal } from "../VisitEventModal";

const SubscriptionList = ({
  subscriptions,
  emptyMessage,
}: {
  subscriptions: GetCustomerSubscriptions["response"]["customerSubscriptionsWithUsage"];
  emptyMessage: string;
}) => {
  if (!subscriptions.length) {
    return (
      <div className="mt-4 text-center text-sm text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="mt-2 grid grid-cols-1 gap-6">
      {subscriptions.map((customerSubscription) => (
        <CustomerSubscriptionCard
          key={customerSubscription.id}
          customerSubscription={customerSubscription}
          onManage={() => {
            console.log("Managing subscription:", customerSubscription.id);
          }}
        />
      ))}
    </div>
  );
};

export const CustomerSubscriptions = ({
  customerId,
}: {
  customerId: number;
}) => {
  const [selectedTab, setSelectedTab] = useState("all");

  const { data: customerSubscriptionsResponse, isLoading } =
    api.customer.useGetCustomerSubscriptions({
      customerId,
    });

  const customerSubscriptions = customerSubscriptionsResponse?.customerSubscriptionsWithUsage ?? [];

  const {
    isOpen: isSubscriptionModalOpen,
    onOpen: onOpenSubscriptionModal,
    onOpenChange: onOpenChangeSubscriptionModal,
  } = useDisclosure();

  const {
    isOpen: isVisitModalOpen,
    onOpen: onOpenVisitModal,
    onOpenChange: onOpenChangeVisitModal,
  } = useDisclosure();

  const filteredSubscriptions = useMemo(() => {
    switch (selectedTab) {
      case "active":
        return customerSubscriptions.filter((sub) => sub.status === "active");
      case "canceled":
        return customerSubscriptions.filter((sub) => sub.status === "canceled");
      case "expired":
        return customerSubscriptions.filter((sub) => sub.validTo < new Date());
      case "all":
        return customerSubscriptions;
      default:
        return [];
    }
  }, [customerSubscriptions, selectedTab]);

  if (isLoading) {
    return (
      <div className="mt-4 text-center text-gray-500">
        {t`Nalagam naročnine...`}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <Tabs
          variant="light"
          classNames={{
            cursor: "bg-green-100 shadow-none",
            tabContent: "text-black",
            tab: "border-1 border-gray-100 rounded-lg",
          }}
        >
          <Tab key="all" title={t`Vse`} />
          <Tab key="active" title={t`Aktivne`} />
          <Tab key="canceled" title={t`Preklicane`} />
          <Tab key="expired" title={t`Potekle`} />
        </Tabs>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {customerSubscriptions.length > 0 && (
            <Button
              className="w-full sm:w-auto"
              onPress={onOpenVisitModal}
              size="sm"
            >
              {t`Zabeleži obisk`}
            </Button>
          )}
          <Button
            className="w-full sm:w-auto"
            color="primary"
            onPress={onOpenSubscriptionModal}
            size="sm"
            data-identifier="add-subscription-button"
          >
            {t`Dodaj naročnino`}
          </Button>
        </div>
      </div>

      <SubscriptionList
        subscriptions={filteredSubscriptions}
        emptyMessage={getEmptyMessage(selectedTab)}
      />

      <CustomerSubscriptionDrawer
        isOpen={isSubscriptionModalOpen}
        onOpenChange={onOpenChangeSubscriptionModal}
        customerId={customerId}
      />

      <VisitEventModal
        isOpen={isVisitModalOpen}
        customerId={customerId}
        onClose={onOpenChangeVisitModal}
      />
    </>
  );
};

const getEmptyMessage = (selectedTab: string) => {
  if (selectedTab === "active") {
    return t`Ni aktivnih naročnin`;
  }
  if (selectedTab === "canceled") {
    return t`Ni preklicanih naročnin`;
  }
  if (selectedTab === "expired") {
    return t`Ni poteklih naročnin`;
  }
  return t`Ni naročnin`;
};
